import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { prodApi, num, KINDS, DISPOSALS, GRADES, errMsg } from "./prodApi";
import { useProd } from "./ProdApp";
import { PButton, PInput, PNum, PSelect, PField, PModal, PTextarea } from "./ProdUi";

const DEFAULT_DISPOSAL = { meat: "sellable", processed: "sellable", byproduct: "byproduct", waste: "waste" };
const dec = (v) => String(v).replace(".", ",");

export function NodeForm({ node, onClose }) {
  const { nodes, reload } = useProd();
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  const isNew = !node?.id;

  useEffect(() => {
    if (!node) { setF(null); return; }
    setF({
      code: node.code || "", name: node.name || "", parent_id: node.parent_id || "", kind: node.kind || "meat",
      coefficient: dec(node.coefficient ?? 1), value_per_kg: dec(node.value_per_kg ?? 0), sale_price_per_kg: dec(node.sale_price_per_kg ?? 0),
      disposal: node.disposal || DEFAULT_DISPOSAL[node.kind || "meat"], notes: node.notes || "", active: node.active !== false,
      grade: node.grade ? String(node.grade) : "", reference_price_per_kg: dec(node.reference_price_per_kg || ""), list_price_per_kg: dec(node.list_price_per_kg || ""),
      target_margin_pct: node.target_margin_pct == null ? "" : dec(node.target_margin_pct), alt_names: node.alt_names || "",
    });
  }, [node]);

  if (!node || !f) return null;
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const setKind = (kind) => setF((s) => ({ ...s, kind, disposal: DEFAULT_DISPOSAL[kind] }));
  // Il pregio propone il coefficiente (1,60 / 1,30 / 1,00 / 0,80) ma resta modificabile per singolo taglio
  const setGrade = (g) => setF((s) => ({ ...s, grade: g, coefficient: g ? String(GRADES.find((x) => String(x.value) === g)?.coeff ?? s.coefficient).replace(".", ",") : s.coefficient }));

  const save = async () => {
    if (!f.code.trim() || !f.name.trim()) { toast.error("Codice e nome sono obbligatori"); return; }
    setBusy(true);
    const body = { ...f, parent_id: f.parent_id || null, coefficient: num(f.coefficient), value_per_kg: num(f.value_per_kg), sale_price_per_kg: num(f.sale_price_per_kg),
      grade: f.grade ? parseInt(f.grade, 10) : null, reference_price_per_kg: num(f.reference_price_per_kg), list_price_per_kg: num(f.list_price_per_kg), target_margin_pct: f.target_margin_pct === "" ? null : num(f.target_margin_pct), competitor_prices: node.competitor_prices || [] };
    try {
      if (isNew) await prodApi.post("/production/nodes", body); else await prodApi.put(`/production/nodes/${node.id}`, body);
      await reload();
      toast.success(isNew ? "Taglio creato" : "Taglio aggiornato");
      onClose();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Eliminare "${node.name}" dall'albero?`)) return;
    try { await prodApi.delete(`/production/nodes/${node.id}`); await reload(); toast.success("Taglio eliminato"); onClose(); } catch (e) { toast.error(errMsg(e)); }
  };

  const isMeat = f.kind === "meat" || f.kind === "processed";
  return (
    <PModal open title={isNew ? "Nuovo taglio" : `Modifica ${node.name}`} onClose={onClose}>
      <div className="grid grid-cols-2 gap-3" data-testid="prod-node-form">
        <PField label="Codice"><PInput value={f.code} onChange={(e) => set("code", e.target.value.toUpperCase())} placeholder="FESA" data-testid="prod-node-code" /></PField>
        <PField label="Tipo">
          <PSelect value={f.kind} onChange={(e) => setKind(e.target.value)} data-testid="prod-node-kind">{KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}</PSelect>
        </PField>
        <PField label="Nome" className="col-span-2"><PInput value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="Fesa / Inside" data-testid="prod-node-name" /></PField>
        <PField label="Taglio padre (posizione nell'albero)" className="col-span-2">
          <PSelect value={f.parent_id} onChange={(e) => set("parent_id", e.target.value)} data-testid="prod-node-parent">
            <option value="">— Radice —</option>
            {nodes.filter((n) => n.id !== node.id).map((n) => <option key={n.id} value={n.id}>{n.name} ({n.code})</option>)}
          </PSelect>
        </PField>
        <PField label="Destinazione predefinita" className="col-span-2">
          <PSelect value={f.disposal} onChange={(e) => set("disposal", e.target.value)} data-testid="prod-node-disposal">{DISPOSALS.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}</PSelect>
        </PField>
        {isMeat ? <>
          <PField label="Pregio commerciale" hint="Propone il coefficiente, poi modificabile"><PSelect value={f.grade} onChange={(e) => setGrade(e.target.value)} data-testid="prod-node-grade"><option value="">—</option>{GRADES.map((g) => <option key={g.value} value={g.value}>{g.label} (coeff. {g.coeff.toFixed(2).replace(".", ",")})</option>)}</PSelect></PField>
          <PField label="Coefficiente economico" hint="Per la ripartizione ponderata (es. filetto 3,00)"><PNum value={f.coefficient} onChange={(e) => set("coefficient", e.target.value)} data-testid="prod-node-coefficient" /></PField>
          <PField label="Prezzo vendita €/kg" hint="Predefinito, modificabile in ricetta"><PNum value={f.sale_price_per_kg} onChange={(e) => set("sale_price_per_kg", e.target.value)} data-testid="prod-node-price" /></PField>
          <PField label="Rif. mercato €/kg" hint="Prezzo di mercato, indipendente dal costo"><PNum value={f.reference_price_per_kg} onChange={(e) => set("reference_price_per_kg", e.target.value)} data-testid="prod-node-reference" /></PField>
          <PField label="Listino corrente €/kg" hint="Prezzo oggi in vigore"><PNum value={f.list_price_per_kg} onChange={(e) => set("list_price_per_kg", e.target.value)} data-testid="prod-node-list-price" /></PField>
          <PField label="Margine obiettivo %" hint="Vuoto = quello dello stadio"><PNum value={f.target_margin_pct} onChange={(e) => set("target_margin_pct", e.target.value)} data-testid="prod-node-target" /></PField>
          <PField label="Altre denominazioni" hint="Nome anatomico, inglese, dialettale" className="col-span-2"><PInput value={f.alt_names} onChange={(e) => set("alt_names", e.target.value)} placeholder="Inside, Topside, Rosa…" data-testid="prod-node-alt" /></PField>
        </> : (
          <PField label="Valore €/kg" hint="Positivo = ricavo (es. ossa 0,20) · negativo = costo smaltimento (es. −0,30)" className="col-span-2"><PNum value={f.value_per_kg} onChange={(e) => set("value_per_kg", e.target.value)} data-testid="prod-node-value" /></PField>
        )}
        <PField label="Note" className="col-span-2"><PTextarea value={f.notes} onChange={(e) => set("notes", e.target.value)} data-testid="prod-node-notes" /></PField>
        <label className="col-span-2 flex items-center gap-3 h-12 text-sm text-slate-300">
          <input type="checkbox" className="w-6 h-6 accent-blue-500" checked={f.active} onChange={(e) => set("active", e.target.checked)} data-testid="prod-node-active" /> Attivo (selezionabile nelle ricette)
        </label>
      </div>
      <div className="flex flex-col-reverse sm:flex-row gap-2 mt-5 sm:justify-between">
        {!isNew ? <PButton variant="danger" onClick={remove} data-testid="prod-node-delete">Elimina</PButton> : <span />}
        <div className="flex gap-2">
          <PButton onClick={onClose}>Annulla</PButton>
          <PButton variant="primary" onClick={save} disabled={busy} data-testid="prod-node-save">Salva</PButton>
        </div>
      </div>
    </PModal>
  );
}

export function TrimForm({ trim, onClose }) {
  const { reload } = useProd();
  const [f, setF] = useState(null);
  useEffect(() => { setF(trim ? { code: trim.code || "", name: trim.name || "", description: trim.description || "" } : null); }, [trim]);
  if (!trim || !f) return null;
  const isNew = !trim.id;
  const save = async () => {
    if (!f.code.trim() || !f.name.trim()) { toast.error("Codice e nome sono obbligatori"); return; }
    try {
      if (isNew) await prodApi.post("/production/trim-levels", f); else await prodApi.put(`/production/trim-levels/${trim.id}`, f);
      await reload(); toast.success("Rifilatura salvata"); onClose();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <PModal open title={isNew ? "Nuova rifilatura" : `Modifica ${trim.name}`} onClose={onClose}>
      <div className="space-y-3" data-testid="prod-trim-form">
        <PField label="Codice"><PInput value={f.code} onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase() })} placeholder="PAD" data-testid="prod-trim-code" /></PField>
        <PField label="Nome"><PInput value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="PAD / Pronto all'uso" data-testid="prod-trim-name" /></PField>
        <PField label="Descrizione"><PTextarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="prod-trim-description" /></PField>
      </div>
      <div className="flex gap-2 mt-5 justify-end">
        <PButton onClick={onClose}>Annulla</PButton>
        <PButton variant="primary" onClick={save} data-testid="prod-trim-save">Salva</PButton>
      </div>
    </PModal>
  );
}
